import type { CashoutQuote, MyBet, MyBetsResponse } from '@ps/web-sports-api-types';

import type { SearchBetsQuery, SportsMyBetsState } from './types';

interface BetsLoadedSuccessfullyPayload extends MyBetsResponse {
  isSettled?: boolean;
}

interface LocationUpdatePayload {
  pathname: string;
}

interface CashedOutSuccessfullyPayload {
  betId: string;
  cashedOutAmount: number;
}

export const reducers = {
  betsLoadedSuccessfully: (
    state: SportsMyBetsState,
    payload: BetsLoadedSuccessfullyPayload
  ): SportsMyBetsState => {
    if (state.isSettled !== payload.isSettled) {
      return state;
    }

    const filteredBets = [...state.bets, ...payload.bets].filter(
      (bet) => !!bet.isSettled === !!state.isSettled
    );

    const uniqueBetIds = [...new Set(filteredBets.map((bet) => bet.betId))];

    const uniqueBets = uniqueBetIds.map((id) =>
      filteredBets.find((bet) => bet.betId === id)
    ) as MyBet[];

    return {
      ...state,
      isFetching: false,
      bets: uniqueBets,
      cashoutQuotes: { ...state.cashoutQuotes, ...payload.cashoutQuotes },
      moreAvailable: payload.moreAvailable,
      numberOfMatches: payload.numberOfMatches
    };
  },
  betsLoadedUnsuccessfully: (state: SportsMyBetsState): SportsMyBetsState => ({
    ...state,
    isFetching: false,
    isErroneous: true
  }),
  betsRequested: (state: SportsMyBetsState, payload: SearchBetsQuery): SportsMyBetsState => ({
    ...state,
    bets: payload.fromRecord === 1 ? [] : state.bets,
    cashoutQuotes: payload.fromRecord === 1 ? {} : state.cashoutQuotes,
    cashedOut: payload.fromRecord === 1 ? {} : state.cashedOut,
    isFetching: true,
    isErroneous: false,
    isSettled: payload.isSettled
  }),
  betsChanged: (state: SportsMyBetsState, payload: LocationUpdatePayload): SportsMyBetsState => {
    const hasMyBetsChanged =
      (payload.pathname.includes('/my-bets/open') && state.isSettled) ||
      (payload.pathname.includes('/my-bets/settled') && !state.isSettled);

    if (hasMyBetsChanged) {
      return {
        ...state,
        isFetching: true,
        isErroneous: false
      };
    }

    return state;
  },
  cashedOutSuccessfully: (
    state: SportsMyBetsState,
    payload: CashedOutSuccessfullyPayload
  ): SportsMyBetsState => ({
    ...state,
    cashedOut: { ...state.cashedOut, [payload.betId]: payload.cashedOutAmount }
  }),
  cashoutQuotesUpdated: (
    state: SportsMyBetsState,
    payload: Record<string, CashoutQuote>
  ): SportsMyBetsState => ({
    ...state,
    cashoutQuotes: payload
  })
};
